import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ColorPickerComponent } from '@app/components/color-picker';
import { ConfirmDialogComponent } from '@app/components/confirm-dialog';
import { DialogComponent } from '@app/components/dialog';
import { FormActionButtonComponent } from '@app/components/form-action-button';
import { TextInputComponent, TextInputWidth } from '@app/components/text-input';
import { AdminChallengeTeamsService } from '@app/services/admin-challenge-teams/admin-challenge-teams.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { TranslateModule } from '@ngx-translate/core';
import { ChallengeBodyTeam } from '@skyfit/core-data-library';
import { ChallengeUsersComponent } from './challenge-users/challenge-users.component';

@Component({
  selector: 'app-challenge-teams',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogComponent,
    ConfirmDialogComponent,
    TranslateModule,
    FormActionButtonComponent,
    ChallengeUsersComponent,
    TextInputComponent,
    ColorPickerComponent,
  ],
  templateUrl: './challenge-teams.component.html',
  styleUrl: './challenge-teams.component.css',
})
export class ChallengeTeamsComponent implements OnInit {
  private readonly teamsService = inject(AdminChallengeTeamsService);
  private readonly translateService = inject(TranslatorService);

  @Input() teams!: ChallengeBodyTeam[];
  @Input() disabled = false;
  @Output() teamsChanged = new EventEmitter<ChallengeBodyTeam[]>();

  newTeam = signal<ChallengeBodyTeam[]>([]);

  selectedTeam = signal<ChallengeBodyTeam>(undefined);
  showAddTeams = signal(false);
  showConfirmDelete = signal(false);
  deleteTeamTitle = computed(() => {
    return this.translateService.translate('challenges.confirmDeleteTeam', [
      this.selectedTeam()?.name,
    ]);
  });
  InputWidth = TextInputWidth;

  ngOnInit() {
    this.newTeam.set(this.teams);
  }

  addTeam() {
    this.teamsService.addTeam(this.teams);
    this.teamsChanged.emit(this.teams);
  }

  addUsers(team: ChallengeBodyTeam): void {
    this.selectedTeam.set(team);
    this.showAddTeams.set(true);
  }

  deleteTeam(team: ChallengeBodyTeam): void {
    this.selectedTeam.set(team);
    this.showConfirmDelete.set(true);
  }

  confirmDeleteClosed(): void {
    this.selectedTeam.set(undefined);
    this.showConfirmDelete.set(false);
  }

  confirmDelete(): void {
    this.teams = this.teams.filter(
      (c) =>
        c.groupChallengeTeamId !== this.selectedTeam()?.groupChallengeTeamId
    );
    this.selectedTeam.set(undefined);
    this.showConfirmDelete.set(false);
    this.teamsChanged.emit(this.teams);
  }

  confirmDeleteCanceled(): void {
    this.selectedTeam.set(undefined);
    this.showConfirmDelete.set(false);
  }

  onTeamMemberChanged(): void {
    this.teamsChanged.emit(this.teams);
    this.showAddTeams.set(false);
  }

  onTeamMemberCanceled(): void {
    this.showAddTeams.set(false);
  }

  onTeamNameChanged(): void {
    this.teamsChanged.emit(this.teams);
  }

  onTeamColorChanged(): void {
    this.teamsChanged.emit(this.teams);
  }
}
